import { ArrowButton, PagesWrapper, PaginatorActiveButton, PaginatorNextButton, PaginatorWrapper } from './styles';
import range from 'lodash/range';
import LeftArrowIcon from '../images/LeftArrowIcon.svg?react';
import RightArrowIcon from '../images/RightArrowIcon.svg?react';
import { useRef } from 'react';
import { useReplaceSearchParams } from '@/hooks/useReplaceSearchParams';
import { PaginatorJump } from '../PaginatorJump/PaginatorJump';

const PAGE_SEPARATOR_COUNT = 1;
const SCROLL_DELAY = 350;

type PaginatorContainerType = {
  page: number;
  limit: number;
  total: number;
};

export const PaginatorContainer = ({ page, limit, total }: PaginatorContainerType) => {
  const { replaceSearchParams } = useReplaceSearchParams();
  const paginatorRef = useRef<HTMLDivElement>(null);
  const totalPagesCount = limit && total ? Math.ceil(total / limit) : 0;
  const leftDelimiter = page > PAGE_SEPARATOR_COUNT + 1;
  const rightDelimiter = totalPagesCount - 1 - page > PAGE_SEPARATOR_COUNT + 1;
  const leftPage = leftDelimiter ? page - PAGE_SEPARATOR_COUNT : 0;
  const rightPage = rightDelimiter ? page + PAGE_SEPARATOR_COUNT + 1 : totalPagesCount;

  const scrollToBottom = () => {
    if (!paginatorRef?.current) return;

    setTimeout(() => {
      paginatorRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, SCROLL_DELAY);
  };

  const replaceUrl = (newSearchParams: object) => {
    replaceSearchParams(newSearchParams);
    scrollToBottom();
  };

  const navigateToPreviousPage = (prevPage: number) => {
    if (prevPage <= 0) return;

    const newPage = prevPage - 1 > 0 ? prevPage - 1 : null;
    const newQueryParams = { page: newPage };

    replaceUrl(newQueryParams);
  };

  const navigateToPage = (currentPage: number) => {
    const newPage = currentPage || null;
    const newSearchParams = {
      page: newPage,
    };

    replaceUrl(newSearchParams);
  };

  const navigateToNextPage = (nextPage: number) => {
    if (nextPage >= totalPagesCount - 1) return;

    const newPage = nextPage + 1 > totalPagesCount - 1 ? totalPagesCount - 1 : nextPage + 1;
    const newSearchParams = { page: newPage };

    replaceUrl(newSearchParams);
  };

  const getPagesRange = () => {
    return range(leftPage, rightPage);
  };

  const pagesRange = getPagesRange();

  return (
    <PaginatorWrapper ref={paginatorRef}>
      <ArrowButton onClick={() => navigateToPreviousPage(page)} disabled={page <= 0} type='button'>
        <LeftArrowIcon />
      </ArrowButton>
      <PagesWrapper>
        {leftDelimiter && (
          <PaginatorNextButton key={0} onClick={() => navigateToPage(0)} type='button'>
            1
          </PaginatorNextButton>
        )}
        {leftDelimiter && <PaginatorJump totalPagesCount={totalPagesCount} onJump={navigateToPage} />}
        {pagesRange.map((pageNumber: number) => {
          const ButtonElement = pageNumber === page ? PaginatorActiveButton : PaginatorNextButton;

          return (
            <ButtonElement key={pageNumber} onClick={() => navigateToPage(pageNumber)} type='button'>
              {pageNumber + 1}
            </ButtonElement>
          );
        })}
        {rightDelimiter && <PaginatorJump totalPagesCount={totalPagesCount} onJump={navigateToPage} />}

        {rightDelimiter && (
          <PaginatorNextButton
            key={totalPagesCount - 1}
            onClick={() => navigateToPage(totalPagesCount - 1)}
            type='button'
          >
            {totalPagesCount}
          </PaginatorNextButton>
        )}
      </PagesWrapper>
      <ArrowButton onClick={() => navigateToNextPage(page)} disabled={page >= totalPagesCount - 1} type='button'>
        <RightArrowIcon />
      </ArrowButton>
    </PaginatorWrapper>
  );
};
